/**
 * Automated Compliance Checking Service
 * Proactive monitoring and gap identification
 */

import LocalStorageService from '../storage/LocalStorageService';
import CareMinutesService from '../care/CareMinutesService';
import StarRatingsService from '../quality/StarRatingsService';
import IncidentAutomationService from '../incidents/IncidentAutomationService';

class AutomatedComplianceService {
  constructor() {
    this.storageKey = 'complianceChecks';
  }

  /**
   * Run comprehensive compliance check
   */
  async runComplianceCheck(facilityId) {
    try {
      const today = new Date().toISOString().split('T')[0];
      const checks = {
        facilityId,
        checkDate: today,
        careMinutes: await this.checkCareMinutes(facilityId, today),
        starRatings: await this.checkStarRatings(facilityId),
        sirs: await this.checkSIRSCompliance(facilityId),
        qualityStandards: await this.checkQualityStandards(facilityId),
        audits: await this.checkAuditReadiness(facilityId)
      };
      
      const overallScore = this.calculateOverallScore(checks);
      const gaps = this.identifyGaps(checks);
      const recommendations = this.generateRecommendations(gaps);
      
      const complianceCheck = {
        id: `cc-${Date.now()}`,
        facilityId,
        checkDate: today,
        checks,
        overallScore,
        gaps,
        recommendations,
        status: overallScore >= 95 ? 'compliant' : overallScore >= 80 ? 'at_risk' : 'non_compliant',
        createdAt: new Date().toISOString()
      };
      
      const created = LocalStorageService.addItem(this.storageKey, complianceCheck);
      return { success: true, data: created };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Check care minutes compliance
   */
  async checkCareMinutes(facilityId, date) {
    try {
      const summary = await CareMinutesService.getFacilityComplianceSummary(facilityId, date);
      if (!summary.success) {
        return { compliant: false, score: 0, issues: ['No care minutes data'] };
      }
      
      const data = summary.data;
      const complianceRate = data.complianceRate || 0;
      const isCompliant = complianceRate >= 95;
      
      const issues = [];
      if (!isCompliant) {
        issues.push(`Compliance rate ${complianceRate.toFixed(1)}% below 95% target`);
      }
      if (data.averageMinutes < 215) {
        issues.push(`Average minutes ${data.averageMinutes.toFixed(0)} below 215 target`);
      }
      if (data.averageRNMinutes < 44) {
        issues.push(`Average RN minutes ${data.averageRNMinutes.toFixed(0)} below 44 target`);
      }
      
      return {
        compliant: isCompliant,
        score: complianceRate,
        data: {
          complianceRate,
          averageMinutes: data.averageMinutes,
          averageRNMinutes: data.averageRNMinutes,
          totalResidents: data.totalResidents,
          compliantResidents: data.fullyCompliantResidents
        },
        issues
      };
    } catch (error) {
      return { compliant: false, score: 0, issues: [error.message] };
    }
  }

  /**
   * Check star ratings compliance
   */
  async checkStarRatings(facilityId) {
    try {
      const rating = await StarRatingsService.getCurrentRating(facilityId);
      if (!rating.success) {
        return { compliant: false, score: 0, issues: ['No star rating data'] };
      }
      
      const data = rating.data;
      const overallRating = data.overallRating || 0;
      const isCompliant = overallRating >= 3.5; // Target: 3.5+ stars
      
      const issues = [];
      if (overallRating < 3.5) {
        issues.push(`Overall rating ${overallRating.toFixed(1)} stars below 3.5 target`);
      }
      if (data.compliance.score <= 2) {
        issues.push(`Compliance component ${data.compliance.score} stars - caps overall rating`);
      }
      if (!data.staffing.meetsBothTargets) {
        issues.push('Staffing component: Not meeting both care minutes targets');
      }
      
      return {
        compliant: isCompliant,
        score: overallRating * 20, // Convert to 0-100 scale
        data: {
          overallRating,
          components: {
            experience: data.residentsExperience.score,
            compliance: data.compliance.score,
            staffing: data.staffing.score,
            quality: data.qualityMeasures.score
          }
        },
        issues
      };
    } catch (error) {
      return { compliant: false, score: 0, issues: [error.message] };
    }
  }

  /**
   * Check SIRS compliance
   */
  async checkSIRSCompliance(facilityId) {
    try {
      // Get pending SIRS incidents
      const incidents = await IncidentAutomationService.getPendingSIRSReports(facilityId);
      
      const issues = [];
      let compliant = true;
      
      if (incidents.success && incidents.data) {
        incidents.data.forEach(incident => {
          if (incident.priority === 'Priority 1') {
            const hoursRemaining = incident.hoursRemaining || 0;
            if (hoursRemaining < 24 && hoursRemaining > 0) {
              issues.push(`Priority 1 incident ${incident.id} due in ${hoursRemaining.toFixed(1)} hours`);
              if (hoursRemaining < 12) {
                compliant = false;
              }
            } else if (hoursRemaining < 0) {
              issues.push(`Priority 1 incident ${incident.id} is OVERDUE by ${Math.abs(hoursRemaining).toFixed(1)} hours`);
              compliant = false;
            }
          }
        });
      }
      
      const score = compliant ? 100 : issues.length > 0 ? 50 : 100;
      
      return {
        compliant,
        score,
        data: {
          pendingPriority1: incidents.success ? incidents.data.filter(i => i.priority === 'Priority 1').length : 0,
          pendingPriority2: incidents.success ? incidents.data.filter(i => i.priority === 'Priority 2').length : 0
        },
        issues
      };
    } catch (error) {
      return { compliant: false, score: 0, issues: [error.message] };
    }
  }

  /**
   * Check quality standards compliance
   */
  async checkQualityStandards(facilityId) {
    try {
      // Simplified quality standards check
      // In production, would check against all 7 Aged Care Quality Standards
      const standards = {
        'Standard 1: Person-centred care': { compliant: true, evidence: 'Care plans in place' },
        'Standard 2: Clinical care': { compliant: true, evidence: 'Clinical assessments current' },
        'Standard 3: Personal care': { compliant: true, evidence: 'Personal care documented' },
        'Standard 4: Services and supports': { compliant: true, evidence: 'Services documented' },
        'Standard 5: Organisation\'s service environment': { compliant: true, evidence: 'Environment suitable' },
        'Standard 6: Feedback and complaints': { compliant: true, evidence: 'Feedback system active' },
        'Standard 7: Governance': { compliant: true, evidence: 'Governance framework in place' }
      };
      
      const compliantCount = Object.values(standards).filter(s => s.compliant).length;
      const totalCount = Object.keys(standards).length;
      const complianceRate = (compliantCount / totalCount) * 100;
      
      return {
        compliant: complianceRate >= 100,
        score: complianceRate,
        data: { standards, compliantCount, totalCount },
        issues: []
      };
    } catch (error) {
      return { compliant: false, score: 0, issues: [error.message] };
    }
  }

  /**
   * Check audit readiness
   */
  async checkAuditReadiness(facilityId) {
    try {
      // Simplified audit readiness check
      const readinessFactors = {
        documentation: { ready: true, score: 90 },
        policies: { ready: true, score: 95 },
        staffTraining: { ready: true, score: 85 },
        incidentReporting: { ready: true, score: 90 },
        medicationManagement: { ready: true, score: 92 }
      };
      
      const averageScore = Object.values(readinessFactors).reduce((sum, f) => sum + f.score, 0) / Object.keys(readinessFactors).length;
      const isReady = averageScore >= 85;
      
      return {
        compliant: isReady,
        score: averageScore,
        data: { readinessFactors, averageScore },
        issues: []
      };
    } catch (error) {
      return { compliant: false, score: 0, issues: [error.message] };
    }
  }

  /**
   * Calculate overall compliance score
   */
  calculateOverallScore(checks) {
    const weights = {
      careMinutes: 0.30,
      starRatings: 0.25,
      sirs: 0.20,
      qualityStandards: 0.15,
      audits: 0.10
    };
    
    const weightedSum = 
      (checks.careMinutes.score * weights.careMinutes) +
      (checks.starRatings.score * weights.starRatings) +
      (checks.sirs.score * weights.sirs) +
      (checks.qualityStandards.score * weights.qualityStandards) +
      (checks.audits.score * weights.audits);
    
    return Math.round(weightedSum);
  }

  /**
   * Identify compliance gaps
   */
  identifyGaps(checks) {
    const gaps = [];
    
    Object.keys(checks).forEach(key => {
      if (key === 'facilityId' || key === 'checkDate') return;
      
      const check = checks[key];
      if (!check.compliant || check.score < 90) {
        gaps.push({
          area: key,
          score: check.score,
          compliant: check.compliant,
          issues: check.issues || [],
          priority: check.score < 70 ? 'high' : check.score < 85 ? 'medium' : 'low'
        });
      }
    });
    
    return gaps;
  }

  /**
   * Generate recommendations
   */
  generateRecommendations(gaps) {
    const recommendations = [];
    
    gaps.forEach(gap => {
      if (gap.area === 'careMinutes') {
        recommendations.push({
          area: 'Care Minutes',
          priority: gap.priority,
          action: 'Review roster assignments and ensure adequate staffing',
          impact: 'Improve care minutes compliance rate',
          timeline: 'Immediate'
        });
      } else if (gap.area === 'starRatings') {
        recommendations.push({
          area: 'Star Ratings',
          priority: gap.priority,
          action: 'Address low-scoring components to improve overall rating',
          impact: 'Increase star rating and facility reputation',
          timeline: '30 days'
        });
      } else if (gap.area === 'sirs') {
        recommendations.push({
          area: 'SIRS Compliance',
          priority: 'high',
          action: 'Complete pending SIRS reports immediately',
          impact: 'Avoid regulatory penalties',
          timeline: 'Immediate'
        });
      }
    });
    
    return recommendations;
  }

  /**
   * Get compliance dashboard data
   */
  async getComplianceDashboard(facilityId) {
    try {
      const latestCheck = await this.getLatestCheck(facilityId);
      if (!latestCheck.success) {
        // Run new check if none exists
        return await this.runComplianceCheck(facilityId);
      }
      
      return latestCheck;
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Get latest compliance check
   */
  async getLatestCheck(facilityId) {
    try {
      const all = LocalStorageService.getAll(this.storageKey);
      const checks = all
        .filter(c => c.facilityId === facilityId)
        .sort((a, b) => new Date(b.checkDate) - new Date(a.checkDate));
      
      if (checks.length > 0) {
        return { success: true, data: checks[0] };
      }
      
      return { success: false, error: 'No compliance checks found' };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }
}

export default new AutomatedComplianceService();
